import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Grid, CircularProgress } from '@mui/material';
import PlatformHealthWidget from '../PlatformHealth/PlatformHealthWidget';
import { PlatformWidget } from '../PlatformHealth/types';
import { getPlatformHealthSummary } from 'service/service';
import {
  mapResponseToPlatformWidgets,
  DEFAULT_TIME_RANGE,
  widgetMatchesSearch,
} from '../PlatformHealth/helpers';
import { deriveStatusKey, STATUS_META } from '../PlatformHealth/PlatformHealthUtils';
import {
  dashboardSectionHeaderSx,
  dashboardHeaderTextSx,
  placeholderCardSx,
} from '../PlatformHealth/styles';

interface SharedWidgetSectionProps {
  title?: string;
  widgets?: PlatformWidget[];
  onOpenDrilldown: (platformId: string) => void;
  searchText?: string;
  severitySelection?: string;
  hideHealthy?: boolean;
  excludeRealTime?: boolean;
  groupFilter?: string;
  timeRange?: string;
  width?: string | object;
  columns?: number;
  showPlaceholders?: boolean;
  children?: React.ReactNode;
  userActionFetching?: boolean;
  minRows?: number;
}

const gridItemSx = {
  display: 'flex',
  minWidth: 0,
  '& > *': {
    width: '100%',
    height: '100%',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
};

const emptyStateSx = {
  width: '100%',
  py: 4,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: 'rgba(0,0,0,0.45)',
  fontSize: '14px',
  fontWeight: 500,
  border: '1px dashed rgba(0,0,0,0.12)',
  borderRadius: '8px',
  backgroundColor: 'rgba(0,0,0,0.02)',
};

const SharedWidgetSection: React.FC<SharedWidgetSectionProps> = ({
  title,
  widgets: providedWidgets,
  onOpenDrilldown,
  searchText = '',
  severitySelection = 'ALL',
  hideHealthy = false,
  excludeRealTime = false,
  groupFilter,
  timeRange = DEFAULT_TIME_RANGE,
  width,
  columns = 4,
  showPlaceholders = false,
  children,
  userActionFetching = false,
  minRows = 0,
}) => {
  const [internalWidgets, setInternalWidgets] = useState<PlatformWidget[]>([]);
  const [loading, setLoading] = useState(false);

  const lastFetchKeyRef = React.useRef<string>('');

  const fetchData = useCallback(async () => {
    if (providedWidgets) return;

    const body = {
      searchText: searchText,
      timeRange: timeRange,
    };
    const fetchKey = JSON.stringify(body);

    if (lastFetchKeyRef.current === fetchKey) return;

    lastFetchKeyRef.current = fetchKey;
    setLoading(true);
    try {
      const res = await getPlatformHealthSummary(body);
      if (res?.success || Array.isArray(res)) {
        const incoming = mapResponseToPlatformWidgets(res?.data ?? res);
        setInternalWidgets(incoming);
      }
    } catch (err) {
      console.error('Error loading common platform health section data:', err);
    } finally {
      setLoading(false);
    }
  }, [providedWidgets, timeRange, searchText]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rawWidgets = providedWidgets || internalWidgets || [];

  const processedWidgets = useMemo(() => {
    let filtered = [...rawWidgets];

    // 1. Exclude real-time (KPI) platforms (explicit opt-in outside Early Warning System)
    if (excludeRealTime && !hideHealthy) {
      filtered = filtered.filter((w) => !w.isRealTime);
    }

    // 2. Early Warning System: exclude both realtime/KPI and healthy platforms
    if (hideHealthy) {
      filtered = filtered.filter((w) => {
        const totalAlerts = w.alertCounts?.total ?? 0;
        if (totalAlerts === 0) return false;                         
        if (deriveStatusKey(w) === 'HEALTHY') return false;  
        return true;
      });
    }

    // 3. Filter by Search Text
    if (searchText && searchText.trim()) {
      filtered = filtered.filter((w) => widgetMatchesSearch(w, searchText));
    }

    // 4. Filter by Severity
    if (severitySelection && severitySelection !== 'ALL') {
      filtered = filtered.filter((w) => deriveStatusKey(w) === severitySelection);
    }

    // 5. Filter by Group (Cloud / On Prem)
    if (groupFilter) {
      const gf = groupFilter.toUpperCase().trim();
      filtered = filtered.filter((w) => {
        const g = (w.group || '').toUpperCase().trim();
        if (gf.includes('PREM')) return g.includes('PREM');
        return g === gf;
      });
    }

    // 6. Sort by Severity Rank
    filtered.sort((a, b) => {
      if (severitySelection !== 'ALL') {
        const isMatchA = deriveStatusKey(a) === severitySelection ? 1 : 0;
        const isMatchB = deriveStatusKey(b) === severitySelection ? 1 : 0;
        if (isMatchB !== isMatchA) return isMatchB - isMatchA;
      }
      const rankA = STATUS_META[deriveStatusKey(a)]?.severityRank ?? 99;
      const rankB = STATUS_META[deriveStatusKey(b)]?.severityRank ?? 99;
      if (rankA !== rankB) return rankA - rankB;
      return (a.platformName ?? '').localeCompare(b.platformName ?? '');
    });

    return filtered;
  }, [rawWidgets, groupFilter, hideHealthy, excludeRealTime, searchText, severitySelection]);

  const placeholderCount = useMemo(() => {
    if (!showPlaceholders) return 0;
    const totalExisting = processedWidgets.length + (children ? 1 : 0);
    const remainder = totalExisting % columns;
    const fillCurrentRow = remainder === 0 ? 0 : columns - remainder;
    const currentRowCount = Math.ceil((totalExisting + fillCurrentRow) / columns);
    const extraRows = Math.max(0, minRows - currentRowCount);
    return fillCurrentRow + extraRows * columns;
  }, [processedWidgets.length, children, columns, showPlaceholders, minRows]);

  const shouldRender = showPlaceholders || !(!loading && processedWidgets.length === 0 && !children);

  if (!shouldRender && !title) return null;

  return (
    <Box sx={{ mt: title ? 0 : 2, pb: 2, width: width || '100%', minWidth: 0, position: 'relative' }}>
      {title && (
        <Box sx={dashboardSectionHeaderSx}>
          <Typography sx={dashboardHeaderTextSx}>{title}</Typography>
        </Box>
      )}

      {loading && (
        <Box
          sx={{
            position: processedWidgets.length > 0 ? 'absolute' : 'relative',
            top: title ? 40 : 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
            backgroundColor: processedWidgets.length > 0 ? 'rgba(255,255,255,0.4)' : 'transparent',
            minHeight: processedWidgets.length > 0 ? 'auto' : '200px',
            borderRadius: '8px',
            backdropFilter: processedWidgets.length > 0 ? 'blur(1px)' : 'none',
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}

      <Grid
        container
        rowSpacing={3}
        columnSpacing={3}
        alignItems="stretch"
        sx={{ opacity: loading && processedWidgets.length > 0 ? 0.6 : 1, transition: 'opacity 0.2s ease' }}
      >
        {children}

        {processedWidgets.map((w) => (
          <Grid item key={w.platformId} xs={12} sm={6} md={4} lg={12 / columns} sx={gridItemSx}>
            <PlatformHealthWidget
              data={w}
              onOpenDrilldown={onOpenDrilldown}
              searchText={searchText}
              isKPI={w.isRealTime}
              kpiItems={w.kpiItems}
              allKpiItems={w.allKpiItems}
            />
          </Grid>
        ))}

        {Array.from({ length: placeholderCount }, (_, i) => `placeholder-${i}`).map((key) => (
          <Grid item key={key} xs={12} sm={6} md={4} lg={12 / columns} sx={gridItemSx}>
            <Box sx={placeholderCardSx}>No Platform</Box>
          </Grid>
        ))}

        {!loading && !userActionFetching && processedWidgets.length === 0 && !children && placeholderCount === 0 && (
          <Grid item xs={12}>
            <Box sx={emptyStateSx}>
              {searchText.trim() ? 'No results found' : 'No platforms available'}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default SharedWidgetSection;
